<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import SlideOver from '@/components/shared/SlideOver.vue'
import GalleryPicture from '@/components/organizer/galleries/GalleryPicture.vue'
import { useGalleries } from '@/composables/useGalleries'
import {
  COVER_HEIC_UNSUPPORTED,
  COVER_PREPARE_FAILED,
  useGalleryCoverUpload,
} from '@/composables/useGalleryCoverUpload'
import { useGalleryToast } from '@/composables/useGalleryToast'
import { CoverMediaStatus, GalleryApiErrorCode, GalleryStatus, GalleryType } from '@/types/galleries'
import type { GalleryPayload, OrganizerGallery, OrganizerGalleryItem, UpdateGalleryPayload } from '@/types/galleries'
import { itemPreviewImage } from '@/utils/galleries/imageSources'

const TITLE_MAX = 150
const SUBTITLE_MAX = 255
const DESCRIPTION_MAX = 2000
const CREDIT_MAX = 150
const EXTERNAL_URL_MAX = 2048
const COVER_ACCEPT = 'image/jpeg,image/png,image/webp'
// HEIC/HEIF is accepted here because the browser converts it before upload.
const COVER_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']

const props = defineProps<{
  open: boolean
  eventSlug: string
  gallery?: OrganizerGallery | null
  /** Items of the gallery, enabling the cover picker in edit mode. */
  items?: OrganizerGalleryItem[]
}>()

const emit = defineEmits<{
  close: []
  created: [gallery: OrganizerGallery]
  updated: [gallery: OrganizerGallery]
}>()

const { t } = useI18n()
const toast = useGalleryToast()
const { create, update, saving, fieldErrors, clearFieldErrors } = useGalleries(props.eventSlug)
const coverUpload = useGalleryCoverUpload(props.eventSlug)

const isEdit = computed(() => props.gallery !== null && props.gallery !== undefined)
const busy = computed(() => saving.value || coverUpload.uploading.value)

const form = reactive({
  title: '',
  subtitle: '',
  description: '',
  default_credit: '',
  status: GalleryStatus.Draft as GalleryStatus,
  cover_item_id: null as string | null,
  type: GalleryType.Media as GalleryType,
  external_url: '',
})
const localErrors = ref<Record<string, string>>({})
const coverInput = ref<HTMLInputElement | null>(null)
const coverFile = ref<File | null>(null)
const coverPreviewUrl = ref<string | null>(null)
const removeCover = ref(false)

const isExternal = computed(() => form.type === GalleryType.External)
const existingCoverStatus = computed(() => (removeCover.value ? null : (props.gallery?.cover_media_status ?? null)))
const existingCover = computed(() => (removeCover.value ? null : (props.gallery?.cover ?? null)))
const hasCoverToRemove = computed(
  () => coverFile.value !== null || (!removeCover.value && (props.gallery?.cover_media_status ?? null) !== null),
)
const coverProcessing = computed(
  () =>
    coverFile.value === null &&
    (existingCoverStatus.value === CoverMediaStatus.Pending || existingCoverStatus.value === CoverMediaStatus.Processing),
)
const coverFailed = computed(() => coverFile.value === null && existingCoverStatus.value === CoverMediaStatus.Failed)
const typeLabel = computed(() => (isExternal.value ? t('galleries.card.external') : t('galleries.form.type_media')))

const coverCandidates = computed(() =>
  (props.items ?? []).filter((item) => itemPreviewImage(item) !== null),
)

function clearCoverSelection(): void {
  if (coverPreviewUrl.value !== null && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(coverPreviewUrl.value)
  }
  coverPreviewUrl.value = null
  coverFile.value = null
  if (coverInput.value !== null) coverInput.value.value = ''
}

onBeforeUnmount(clearCoverSelection)

watch(
  () => props.open,
  (open) => {
    if (!open) {
      clearCoverSelection()
      return
    }
    clearFieldErrors()
    localErrors.value = {}
    const gallery = props.gallery
    form.title = gallery?.title ?? ''
    form.subtitle = gallery?.subtitle ?? ''
    form.description = gallery?.description ?? ''
    form.default_credit = gallery?.default_credit ?? ''
    form.status = gallery?.status ?? GalleryStatus.Draft
    form.cover_item_id = gallery?.cover_item_id ?? null
    form.type = gallery?.type ?? GalleryType.Media
    form.external_url = gallery?.external_url ?? ''
    clearCoverSelection()
    removeCover.value = false
  },
  { immediate: true },
)

function errorFor(field: string): string | null {
  return localErrors.value[field] ?? fieldErrors.value[field]?.[0] ?? null
}

function isHttpsUrl(value: string): boolean {
  if (!value.startsWith('https://')) return false
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}

function validate(): boolean {
  const errors: Record<string, string> = {}
  if (form.title.trim() === '') errors['title'] = t('common.required_field')
  if (form.title.length > TITLE_MAX) errors['title'] = t('galleries.form.too_long', { max: TITLE_MAX })
  if (form.subtitle.length > SUBTITLE_MAX) errors['subtitle'] = t('galleries.form.too_long', { max: SUBTITLE_MAX })
  if (form.description.length > DESCRIPTION_MAX) errors['description'] = t('galleries.form.too_long', { max: DESCRIPTION_MAX })
  if (!isExternal.value && form.default_credit.length > CREDIT_MAX) {
    errors['default_credit'] = t('galleries.form.too_long', { max: CREDIT_MAX })
  }
  if (isExternal.value) {
    const url = form.external_url.trim()
    if (url === '') errors['external_url'] = t('common.required_field')
    else if (url.length > EXTERNAL_URL_MAX) errors['external_url'] = t('galleries.form.too_long', { max: EXTERNAL_URL_MAX })
    else if (!isHttpsUrl(url)) errors['external_url'] = t('galleries.form.external_url_https')
  }
  localErrors.value = errors
  return Object.keys(errors).length === 0
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

function onCoverChosen(event: Event): void {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0] ?? null
  if (file === null) return
  const { cover: _previous, ...rest } = localErrors.value
  localErrors.value = rest
  if (!COVER_ALLOWED_TYPES.includes(file.type.toLowerCase())) {
    localErrors.value = { ...rest, cover: t('galleries.reasons.unsupported_type') }
    input.value = ''
    return
  }
  clearCoverSelection()
  coverFile.value = file
  if (typeof URL.createObjectURL === 'function') coverPreviewUrl.value = URL.createObjectURL(file)
  removeCover.value = false
}

function markCoverRemoved(): void {
  clearCoverSelection()
  removeCover.value = true
}

function coverErrorMessage(code: string | null): string {
  switch (code) {
    case GalleryApiErrorCode.StorageQuotaExceeded:
      return t('galleries.reasons.not_enough_storage')
    case COVER_HEIC_UNSUPPORTED:
      return t('galleries.reasons.heic_unsupported')
    case COVER_PREPARE_FAILED:
      return t('galleries.reasons.prepare_failed')
    case GalleryApiErrorCode.FileTooLarge:
    case GalleryApiErrorCode.TooLarge:
    case GalleryApiErrorCode.SizeMismatch:
      return t('galleries.reasons.file_too_large')
    case GalleryApiErrorCode.ContentInvalid:
      return t('galleries.reasons.content_mismatch')
    default:
      return t('galleries.cover.upload_failed_after_save')
  }
}

function handleClose(): void {
  if (busy.value) return
  emit('close')
}

/**
 * Applies the cover changes after the gallery itself was saved. A failure never loses the
 * saved gallery: the caller still receives it, and an error toast explains what went wrong.
 */
async function applyCoverChanges(saved: OrganizerGallery): Promise<OrganizerGallery> {
  let latest = saved
  let failedCode: string | null = null

  if (removeCover.value && coverFile.value === null) {
    if (await coverUpload.removeCover(saved.id)) {
      latest = { ...latest, cover: null, cover_media_status: null }
    } else {
      failedCode = coverUpload.errorCode.value ?? ''
    }
  }

  if (failedCode === null && coverFile.value !== null) {
    const withCover = await coverUpload.upload(saved.id, coverFile.value)
    if (withCover !== null) latest = withCover
    else failedCode = coverUpload.errorCode.value ?? ''
  }

  if (failedCode !== null) toast.push(coverErrorMessage(failedCode === '' ? null : failedCode), { kind: 'error' })
  return latest
}

async function handleSubmit(): Promise<void> {
  if (!validate()) return

  const common = {
    title: form.title.trim(),
    subtitle: blankToNull(form.subtitle),
    description: blankToNull(form.description),
    status: form.status,
  }

  if (isEdit.value && props.gallery) {
    // The type is fixed at creation and is never sent on update.
    const payload: UpdateGalleryPayload = isExternal.value
      ? { ...common, external_url: form.external_url.trim() }
      : { ...common, default_credit: blankToNull(form.default_credit) }
    if (!isExternal.value && props.items !== undefined && form.cover_item_id !== (props.gallery.cover_item_id ?? null)) {
      payload.cover_item_id = form.cover_item_id
    }
    const updated = await update(props.gallery.id, payload)
    if (updated === null) return
    emit('updated', isExternal.value ? await applyCoverChanges(updated) : updated)
    return
  }

  const payload: GalleryPayload = isExternal.value
    ? { ...common, type: GalleryType.External, external_url: form.external_url.trim() }
    : { ...common, type: GalleryType.Media, default_credit: blankToNull(form.default_credit) }
  const created = await create(payload)
  if (created === null) return
  emit('created', isExternal.value ? await applyCoverChanges(created) : created)
}
</script>

<template>
  <SlideOver
    :open="open"
    :title="isEdit ? t('galleries.form.edit_title') : t('galleries.form.create_title')"
    :saving="busy"
    @close="handleClose"
  >
    <form id="gallery-form" class="space-y-5" novalidate @submit.prevent="handleSubmit">
      <fieldset v-if="!isEdit" data-testid="gallery-type-fieldset">
        <legend class="block text-sm font-medium text-slate-300 mb-2">{{ t('galleries.form.type') }}</legend>
        <div class="space-y-2">
          <label class="flex items-start gap-3 rounded-lg border border-slate-700 px-3 py-2.5 cursor-pointer has-checked:border-emerald-500">
            <input v-model="form.type" type="radio" name="gallery-type" :value="GalleryType.Media" class="mt-1 accent-emerald-500" />
            <span>
              <span class="block text-sm text-white">{{ t('galleries.form.type_media') }}</span>
              <span class="block text-xs text-slate-400">{{ t('galleries.form.type_media_hint') }}</span>
            </span>
          </label>
          <label class="flex items-start gap-3 rounded-lg border border-slate-700 px-3 py-2.5 cursor-pointer has-checked:border-emerald-500">
            <input v-model="form.type" type="radio" name="gallery-type" :value="GalleryType.External" class="mt-1 accent-emerald-500" />
            <span>
              <span class="block text-sm text-white">{{ t('galleries.form.type_external') }}</span>
              <span class="block text-xs text-slate-400">{{ t('galleries.form.type_external_hint') }}</span>
            </span>
          </label>
        </div>
        <p v-if="errorFor('type')" class="mt-1 text-xs text-red-400">{{ errorFor('type') }}</p>
      </fieldset>
      <p v-else class="text-xs text-slate-400" data-testid="gallery-type-readonly">
        {{ t('galleries.form.type_readonly', { type: typeLabel }) }}
      </p>

      <div>
        <label for="gallery-title" class="block text-sm font-medium text-slate-300 mb-1.5">
          {{ t('galleries.form.title') }}
        </label>
        <input
          id="gallery-title"
          v-model="form.title"
          type="text"
          :maxlength="TITLE_MAX"
          class="w-full bg-slate-800 border text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none placeholder:text-slate-500"
          :class="errorFor('title') ? 'border-red-500' : 'border-slate-700'"
          :aria-invalid="errorFor('title') !== null"
          aria-describedby="gallery-title-error"
        />
        <p v-if="errorFor('title')" id="gallery-title-error" class="mt-1 text-xs text-red-400">{{ errorFor('title') }}</p>
      </div>

      <div>
        <label for="gallery-subtitle" class="block text-sm font-medium text-slate-300 mb-1.5">
          {{ t('galleries.form.subtitle') }}
          <span class="text-slate-500 font-normal">({{ t('common.optional') }})</span>
        </label>
        <input
          id="gallery-subtitle"
          v-model="form.subtitle"
          type="text"
          :maxlength="SUBTITLE_MAX"
          class="w-full bg-slate-800 border text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
          :class="errorFor('subtitle') ? 'border-red-500' : 'border-slate-700'"
        />
        <p v-if="errorFor('subtitle')" class="mt-1 text-xs text-red-400">{{ errorFor('subtitle') }}</p>
      </div>

      <div>
        <label for="gallery-description" class="block text-sm font-medium text-slate-300 mb-1.5">
          {{ t('galleries.form.description') }}
          <span class="text-slate-500 font-normal">({{ t('common.optional') }})</span>
        </label>
        <textarea
          id="gallery-description"
          v-model="form.description"
          rows="4"
          :maxlength="DESCRIPTION_MAX"
          class="w-full bg-slate-800 border text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
          :class="errorFor('description') ? 'border-red-500' : 'border-slate-700'"
        />
        <div class="mt-1 flex items-start justify-between gap-3">
          <p v-if="errorFor('description')" class="text-xs text-red-400">{{ errorFor('description') }}</p>
          <span v-else />
          <span class="text-xs text-slate-500 shrink-0">{{ form.description.length }} / {{ DESCRIPTION_MAX }}</span>
        </div>
      </div>

      <div v-if="isExternal">
        <label for="gallery-external-url" class="block text-sm font-medium text-slate-300 mb-1.5">
          {{ t('galleries.form.external_url') }}
        </label>
        <input
          id="gallery-external-url"
          v-model="form.external_url"
          type="url"
          inputmode="url"
          autocomplete="url"
          placeholder="https://"
          :maxlength="EXTERNAL_URL_MAX"
          class="w-full bg-slate-800 border text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none placeholder:text-slate-500"
          :class="errorFor('external_url') ? 'border-red-500' : 'border-slate-700'"
          :aria-invalid="errorFor('external_url') !== null"
          aria-describedby="gallery-external-url-error"
        />
        <p v-if="errorFor('external_url')" id="gallery-external-url-error" class="mt-1 text-xs text-red-400">{{ errorFor('external_url') }}</p>
        <p v-else class="mt-1 text-xs text-slate-500">{{ t('galleries.form.external_url_hint') }}</p>
      </div>

      <fieldset v-if="isExternal" data-testid="gallery-cover-upload">
        <legend class="block text-sm font-medium text-slate-300 mb-2">
          {{ t('galleries.form.cover_image') }}
          <span class="text-slate-500 font-normal">({{ t('common.optional') }})</span>
        </legend>
        <div class="flex items-start gap-4">
          <div class="w-28 aspect-[4/3] shrink-0 overflow-hidden rounded-lg border border-slate-700 bg-slate-900 flex items-center justify-center">
            <img v-if="coverPreviewUrl" :src="coverPreviewUrl" alt="" class="h-full w-full object-cover" />
            <GalleryPicture v-else-if="existingCover" :image="existingCover" sizes="112px" />
            <span v-else class="px-1 text-center text-xs text-slate-500">{{ t('galleries.cover.none') }}</span>
          </div>
          <div class="min-w-0 space-y-2">
            <input
              ref="coverInput"
              type="file"
              :accept="COVER_ACCEPT"
              class="sr-only"
              tabindex="-1"
              data-testid="gallery-cover-input"
              @change="onCoverChosen"
            />
            <div class="flex flex-wrap items-center gap-3">
              <button
                type="button"
                :disabled="busy"
                class="border border-slate-600 hover:border-slate-500 text-slate-200 font-semibold rounded-lg px-3 py-1.5 text-sm transition-colors disabled:opacity-50"
                @click="coverInput?.click()"
              >
                {{ t('galleries.form.cover_choose') }}
              </button>
              <button
                v-if="hasCoverToRemove"
                type="button"
                :disabled="busy"
                class="text-sm font-medium text-red-400 hover:text-red-300 disabled:opacity-50"
                data-testid="gallery-cover-remove"
                @click="markCoverRemoved"
              >
                {{ t('galleries.form.cover_remove') }}
              </button>
            </div>
            <p v-if="coverProcessing" class="text-xs text-amber-300">{{ t('galleries.cover.processing') }}</p>
            <p v-else-if="coverFailed" class="text-xs text-red-400">{{ t('galleries.cover.failed') }}</p>
            <p v-if="coverUpload.uploading.value" class="text-xs text-slate-300" role="status">
              {{ t('galleries.tile.uploading', { percent: coverUpload.progress.value }) }}
            </p>
            <p v-if="errorFor('cover')" class="text-xs text-red-400">{{ errorFor('cover') }}</p>
            <p v-else class="text-xs text-slate-500">{{ t('galleries.form.cover_image_hint') }}</p>
          </div>
        </div>
      </fieldset>

      <div v-if="!isExternal">
        <label for="gallery-credit" class="block text-sm font-medium text-slate-300 mb-1.5">
          {{ t('galleries.form.default_credit') }}
          <span class="text-slate-500 font-normal">({{ t('common.optional') }})</span>
        </label>
        <input
          id="gallery-credit"
          v-model="form.default_credit"
          type="text"
          :maxlength="CREDIT_MAX"
          class="w-full bg-slate-800 border text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
          :class="errorFor('default_credit') ? 'border-red-500' : 'border-slate-700'"
        />
        <p v-if="errorFor('default_credit')" class="mt-1 text-xs text-red-400">{{ errorFor('default_credit') }}</p>
        <p v-else class="mt-1 text-xs text-slate-500">{{ t('galleries.form.default_credit_hint') }}</p>
      </div>

      <fieldset>
        <legend class="block text-sm font-medium text-slate-300 mb-2">{{ t('galleries.form.visibility') }}</legend>
        <div class="space-y-2">
          <label class="flex items-start gap-3 rounded-lg border border-slate-700 px-3 py-2.5 cursor-pointer has-checked:border-emerald-500">
            <input v-model="form.status" type="radio" name="gallery-status" :value="GalleryStatus.Draft" class="mt-1 accent-emerald-500" />
            <span>
              <span class="block text-sm text-white">{{ t('galleries.form.draft') }}</span>
              <span class="block text-xs text-slate-400">{{ t('galleries.form.draft_hint') }}</span>
            </span>
          </label>
          <label class="flex items-start gap-3 rounded-lg border border-slate-700 px-3 py-2.5 cursor-pointer has-checked:border-emerald-500">
            <input v-model="form.status" type="radio" name="gallery-status" :value="GalleryStatus.Published" class="mt-1 accent-emerald-500" />
            <span>
              <span class="block text-sm text-white">{{ t('galleries.form.published') }}</span>
              <span class="block text-xs text-slate-400">{{ t('galleries.form.published_hint') }}</span>
            </span>
          </label>
        </div>
        <p v-if="errorFor('status')" class="mt-1 text-xs text-red-400">{{ errorFor('status') }}</p>
      </fieldset>

      <fieldset v-if="isEdit && !isExternal && items !== undefined">
        <legend class="block text-sm font-medium text-slate-300 mb-2">{{ t('galleries.form.cover') }}</legend>
        <div class="grid grid-cols-4 gap-2 max-h-56 overflow-y-auto">
          <label class="relative cursor-pointer">
            <input v-model="form.cover_item_id" type="radio" name="gallery-cover" :value="null" class="peer sr-only" />
            <span class="flex aspect-square items-center justify-center rounded-lg border border-slate-700 bg-slate-900 text-xs text-slate-300 text-center p-1 peer-checked:border-emerald-500 peer-checked:ring-2 peer-checked:ring-emerald-500 peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-500">
              {{ t('galleries.form.cover_auto') }}
            </span>
          </label>
          <label v-for="item in coverCandidates" :key="item.id" class="relative cursor-pointer">
            <input v-model="form.cover_item_id" type="radio" name="gallery-cover" :value="item.id" class="peer sr-only" />
            <span class="block aspect-square overflow-hidden rounded-lg border border-slate-700 bg-slate-900 peer-checked:border-emerald-500 peer-checked:ring-2 peer-checked:ring-emerald-500 peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-500">
              <GalleryPicture v-if="itemPreviewImage(item)" :image="itemPreviewImage(item)!" :alt="item.alt_text ?? item.original_filename ?? ''" sizes="120px" />
            </span>
          </label>
        </div>
        <p v-if="errorFor('cover_item_id')" class="mt-1 text-xs text-red-400">{{ errorFor('cover_item_id') }}</p>
      </fieldset>
    </form>

    <template #footer>
      <div class="flex items-center justify-end gap-3">
        <button
          type="button"
          :disabled="busy"
          class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
          @click="handleClose"
        >
          {{ t('common.cancel') }}
        </button>
        <button
          type="submit"
          form="gallery-form"
          :disabled="busy"
          class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
        >
          <AppSpinner v-if="busy" size="button" />
          {{ busy ? t('common.saving') : isEdit ? t('common.save') : t('galleries.form.create_submit') }}
        </button>
      </div>
    </template>
  </SlideOver>
</template>
